import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { customerApi } from '../api/services';
import Loader from '../components/Loader';
import { Eye, Phone, MessageSquare, Wallet, ShoppingBag, FileText, Heart, Bell, Gift, IndianRupee, Clock, TrendingDown, ChevronDown, Contact, UserRound, RefreshCw } from 'lucide-react';
import formatNumber from '../utils/formatNumber';
import { recheckPayment } from '../utils/razorpayRecheck';
import '../styles/CustomerDetail.css';

const fallbackSvg = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40"><rect width="40" height="40" fill="%23e5e7eb" rx="20"/><text x="50%" y="55%" dominant-baseline="middle" text-anchor="middle" font-size="16" fill="%23999">?</text></svg>';

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('call');
  const [walletSubTab, setWalletSubTab] = useState('transaction');
  const [openAccordion, setOpenAccordion] = useState(null);
  const [recheckingId, setRecheckingId] = useState(null);
  const [introOffer, setIntroOffer] = useState(null);
  const [restoringOffer, setRestoringOffer] = useState(false);

  // Intro offer card (server: controllers/admin/introOfferController.js).
  // Never blocks the page: on any error the card is simply not shown.
  const loadIntroOffer = () => customerApi.getIntroOffer(id)
    .then(res => setIntroOffer(res.data?.data || null))
    .catch(() => setIntroOffer(null));

  useEffect(() => { loadIntroOffer(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const restoreIntroOffer = async () => {
    if (restoringOffer) return;
    if (!window.confirm('Give the intro offer back to this customer?\n\nThey will see the offer again in the app and can use it once more. This does NOT refund any money — use Recharge wallet for that if needed.')) return;
    setRestoringOffer(true);
    try {
      const res = await customerApi.restoreIntroOffer({ userId: id });
      alert(res.data?.message || 'Intro offer given back.');
      await loadIntroOffer();
      load(false);
    } catch (err) {
      alert(err.response?.data?.message || 'Could not give the intro offer back. Please try again.');
    }
    setRestoringOffer(false);
  };

  const load = (showLoader = true) => {
    if (showLoader) setLoading(true);
    return customerApi.getById(id)
      .then(res => {
        const result = res.data.result;
        setData(result && result.length > 0 ? result[0] : null);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Payment logs: ask the server to check a pending/failed Razorpay payment now.
  const handleRecheck = async (p, userName) => {
    if (recheckingId) return;
    setRecheckingId(p.id);
    try { await recheckPayment({ id: p.id, amount: p.amount, userName }, () => load(false)); }
    finally { setRecheckingId(null); }
  };

  if (loading) return <Loader text="Loading customer details..." />;
  if (!data) return <div className="cd-empty">Customer not found.</div>;

  const curr = data.currencySymbol || '\u20B9';
  const callHistory = data.callRequest?.callHistory || [];
  const chatHistory = data.chatRequest?.chatHistory || [];
  const reportHistory = data.reportRequest?.reportHistory || [];
  const wallet = data.walletTransaction?.wallet || [];
  const payment = data.paymentLogs?.payment || [];
  const orders = data.orders?.order || [];
  const pujaOrders = data.pujaOrders?.orders || [];
  const gifts = data.sendGifts?.gifts || [];
  const followers = data.follower || [];
  const notifications = data.notification || [];

  const formatDate = (d) => {
    if (!d) return '-';
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return `${String(dt.getDate()).padStart(2, '0')}-${String(dt.getMonth() + 1).padStart(2, '0')}-${dt.getFullYear()}`;
  };
  const formatDateTime = (d) => {
    if (!d) return '-';
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    let hr = dt.getHours(); const min = String(dt.getMinutes()).padStart(2, '0');
    const ampm = hr >= 12 ? 'pm' : 'am'; hr = hr % 12 || 12;
    return `${formatDate(d)} ${hr}:${min} ${ampm}`;
  };

  // Intro offer tag on a chat / call card (see the Intro offer card above the tabs).
  const offerTag = (c) => {
    if (Number(c.isOfferSession) !== 1) return null;
    const v = Number(c.offerVoided) || 0;
    const text = v === 1 ? 'Intro offer · refunded (short)' : v === 2 ? 'Intro offer · given back' : 'Intro offer';
    return <span className={`cd-offer-tag ${v ? 'voided' : ''}`}>🎁 {text}</span>;
  };

  const statusClass = (status) => {
    if (!status) return 'cd-badge-default';
    const s = status.toLowerCase();
    if (['completed', 'success', 'accepted'].includes(s)) return 'cd-badge-green';
    if (['cancelled', 'failed', 'rejected'].includes(s)) return 'cd-badge-red';
    if (['pending', 'waiting'].includes(s)) return 'cd-badge-yellow';
    return 'cd-badge-default';
  };

  const getWalletDesc = (w) => {
    const type = (w.transactionType || '').toLowerCase();
    if (type === 'gift') return `Gift to ${w.astrologerName || 'Astrologer'}`;
    if (type === 'cashback') return 'Cashback';
    if (type === 'referral') return 'Referral Bonus';
    if (type === 'pujaorder') return 'Puja Order';
    if (type === 'report') return `Report - ${w.astrologerName || ''}`;
    if (type === 'call' || type === 'chat') return `${type.charAt(0).toUpperCase() + type.slice(1)} with ${w.astrologerName || 'Astrologer'} (${w.totalMin || 0} min)`;
    if (type === 'recharge') return 'Wallet Recharge';
    return w.transactionType || '-';
  };

  const tabs = [
    { key: 'call', label: 'Calls', icon: Phone, count: data.callRequest?.totalCount || 0 },
    { key: 'chat', label: 'Chats', icon: MessageSquare, count: data.chatRequest?.totalCount || 0 },
    { key: 'wallet', label: 'Wallet', icon: Wallet, count: data.walletTransaction?.totalCount || 0 },
    { key: 'order', label: 'Orders', icon: ShoppingBag, count: data.orders?.totalCount || 0 },
    { key: 'puja', label: 'Puja', icon: Heart, count: data.pujaOrders?.totalCount || 0 },
    { key: 'report', label: 'Reports', icon: FileText, count: data.reportRequest?.totalCount || 0 },
    { key: 'following', label: 'Following', icon: Heart, count: followers.length },
    { key: 'notification', label: 'Alerts', icon: Bell, count: notifications.length },
    { key: 'gift', label: 'Gifts', icon: Gift, count: gifts.length },
  ];

  const IMAGE_HOST = process.env.REACT_APP_IMAGE_URL;
  const imgSrc = (p) => {
    if (!p) return '';
    if (p.startsWith('http') || p.startsWith('data:')) return p;
    let clean = p.replace(/^\/+/, '');
    // bare filename (no folder) lives under storage/images
    if (!clean.includes('/')) clean = `storage/images/${clean}`;
    // backend serves uploads from /public (customer paths omit it)
    if (!clean.startsWith('public/')) clean = `public/${clean}`;
    return `${IMAGE_HOST}/${clean}`;
  };
  const stats = [
    { label: 'Wallet', value: `${curr}${formatNumber(data.walletBalance || 0)}`, color: '#059669', bg: '#ecfdf5' },
    { label: 'Calls', value: data.callRequest?.totalCount || 0, color: '#7c3aed', bg: '#f5f3ff' },
    { label: 'Chats', value: data.chatRequest?.totalCount || 0, color: '#2563eb', bg: '#eff6ff' },
    { label: 'Orders', value: data.orders?.totalCount || 0, color: '#d97706', bg: '#fffbeb' },
    { label: 'Reports', value: data.reportRequest?.totalCount || 0, color: '#dc2626', bg: '#fef2f2' },
  ];

  return (
    <div className="cd-page">
      {/* Hero Profile */}
      <div className="cd-hero-wrap">
        <div className="cd-hero">
          <div className="cd-hero-left">
            <div className="cd-hero-avatar-wrap">
              {data.profile ? (
                <img src={imgSrc(data.profile)} alt={data.name} className="cd-hero-avatar"
                  onError={e => { e.target.src = fallbackSvg; }} />
              ) : (
                <div className="cd-hero-avatar cd-hero-initial">
                  {data.name ? data.name.charAt(0).toUpperCase() : '?'}
                </div>
              )}
            </div>
            <div className="cd-hero-info">
              <h2 className="cd-hero-name">{data.name || 'Unknown'}</h2>
              <p className="cd-hero-phone">{data.countryCode} {data.contactNo}</p>
              {data.email && <p className="cd-hero-email">{data.email}</p>}
            </div>
          </div>
        </div>
        <div className="cd-hero-stats">
          {stats.map((s, i) => (
            <div key={i} className="cd-stat-chip" style={{ background: s.bg, color: s.color }}>
              <span className="cd-stat-val">{s.value}</span>
              <span className="cd-stat-lbl">{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Info Accordions */}
      <div className="cd-accordion-grid">
        <div className="cd-accordion">
          <button className={`cd-accordion-header ${openAccordion === 'contact' ? 'open' : ''}`} onClick={() => setOpenAccordion(openAccordion === 'contact' ? null : 'contact')}>
            <span><Contact size={16} /> Contact Information</span>
            <ChevronDown size={16} className="cd-accordion-arrow" />
          </button>
          <div className={`cd-accordion-body ${openAccordion === 'contact' ? 'open' : ''}`}>
            <div className="cd-accordion-inner">
              <InfoRow label="Email" value={data.email} />
              <InfoRow label="Address" value={data.addressLine1} />
              <InfoRow label="City" value={data.location} />
              <InfoRow label="Pincode" value={data.pincode} />
              <InfoRow label="Country" value={data.country} />
            </div>
          </div>
        </div>
        <div className="cd-accordion">
          <button className={`cd-accordion-header ${openAccordion === 'personal' ? 'open' : ''}`} onClick={() => setOpenAccordion(openAccordion === 'personal' ? null : 'personal')}>
            <span><UserRound size={16} /> Personal Information</span>
            <ChevronDown size={16} className="cd-accordion-arrow" />
          </button>
          <div className={`cd-accordion-body ${openAccordion === 'personal' ? 'open' : ''}`}>
            <div className="cd-accordion-inner">
              <InfoRow label="Gender" value={data.gender} />
              <InfoRow label="Birth Date" value={formatDate(data.birthDate)} />
              <InfoRow label="Birth Time" value={data.birthTime} />
              <InfoRow label="Birth Place" value={data.birthPlace} />
              <InfoRow label="Joined" value={formatDate(data.created_at)} />
            </div>
          </div>
        </div>
      </div>

      {/* Intro offer — shown while the offer is on, or when this customer had one */}
      {introOffer?.ready && (introOffer.offerMode || introOffer.sessions?.length > 0) && (() => {
        const o = introOffer;
        const used = o.usedOn;
        const status = o.availableNow ? { text: 'Available now', cls: 'cd-badge-green' }
          : used ? { text: 'Used', cls: 'cd-badge-yellow' }
          : o.otherCompletedSessions > 0 ? { text: 'Not a new customer', cls: 'cd-badge-default' }
          : !o.offerMode ? { text: 'Offer is off', cls: 'cd-badge-default' }
          : { text: 'Not available', cls: 'cd-badge-default' };
        const length = (s) => (s.durationSec != null ? `${s.durationSec} sec` : `${s.totalMin || 0} min`);
        return (
          <div className="cd-offer-card">
            <div className="cd-offer-head">
              <span className="cd-offer-title"><Gift size={16} /> Intro offer{o.offerMode && o.price ? ` · ${curr}${formatNumber(o.price)} for ${o.minutes} min` : ''}</span>
              <span className={`cd-badge ${status.cls}`}>{status.text}</span>
            </div>
            {used && (
              <p className="cd-offer-line">
                Used on a {used.kind} with <strong>{used.astrologerName || 'astrologer'}</strong>, {formatDateTime(used.created_at)} · {length(used)} · charged {curr}{formatNumber(used.deduction || 0)}
              </p>
            )}
            {o.otherCompletedSessions > 0 && (
              <p className="cd-offer-line">Has {o.otherCompletedSessions} normal paid / free session(s), so the intro offer does not apply.</p>
            )}
            <p className="cd-offer-line cd-offer-muted">
              Short sessions refunded automatically: {o.shortRetriesUsed} of {o.shortRetriesAllowed}
              {o.minSeconds > 0 ? ` (an offer chat/call under ${o.minSeconds} sec is refunded and the offer kept)` : ' (automatic refund is off)'}
            </p>
            {o.canRestore && (
              <div className="cd-offer-actions">
                <button type="button" className="cd-offer-btn" onClick={restoreIntroOffer} disabled={restoringOffer}>
                  <RefreshCw size={14} /> {restoringOffer ? 'Giving back…' : 'Give intro offer again'}
                </button>
                <span className="cd-offer-muted">For complaints like "no audio, the offer got used". Does not refund money.</span>
              </div>
            )}
          </div>
        );
      })()}

      {/* Tabs */}
      <div className="cd-tabs-wrap">
        <div className="cd-tabs">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`cd-tab ${activeTab === tab.key ? 'active' : ''}`}>
                <Icon size={15} />
                <span className="cd-tab-label">{tab.label}</span>
                {tab.count > 0 && <span className="cd-tab-count">{tab.count}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="cd-content">

        {/* Call History */}
        {activeTab === 'call' && (
          callHistory.length > 0 ? (
            <div className="cd-grid">
              {callHistory.map((c, i) => (
                <div key={i} className="cd-item-card">
                  <div className="cd-item-top">
                    <img src={imgSrc(c.profileImage)} alt="" className="cd-item-avatar"
                      onError={e => { e.target.src = fallbackSvg; }} />
                    <div className="cd-item-meta">
                      <span className="cd-item-name">{c.astrologerName}</span>
                      <span className="cd-item-time">{formatDateTime(c.created_at)}</span>
                      {offerTag(c)}
                    </div>
                    <span className={`cd-badge ${statusClass(c.status)}`}>{c.status || '-'}</span>
                  </div>
                  <div className="cd-item-body">
                    <div className="cd-item-stat">
                      <IndianRupee size={13} /> <span>{curr}{formatNumber(c.callRate || 0)}/min</span>
                    </div>
                    <div className="cd-item-stat">
                      <Clock size={13} /> <span>{c.totalMin || 0} min</span>
                    </div>
                    <div className="cd-item-stat cd-item-stat-red">
                      <TrendingDown size={13} /> <span>-{curr}{formatNumber(c.deduction || 0)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="cd-empty">No call history.</p>
        )}

        {/* Chat History */}
        {activeTab === 'chat' && (
          chatHistory.length > 0 ? (
            <div className="cd-grid">
              {chatHistory.map((c, i) => (
                <div key={i} className="cd-item-card">
                  <div className="cd-item-top">
                    <img src={imgSrc(c.profileImage)} alt="" className="cd-item-avatar"
                      onError={e => { e.target.src = fallbackSvg; }} />
                    <div className="cd-item-meta">
                      <span className="cd-item-name">{c.astrologerName}</span>
                      <span className="cd-item-time">{formatDateTime(c.created_at)}</span>
                      {offerTag(c)}
                    </div>
                    <span className={`cd-badge ${statusClass(c.status)}`}>{c.status || '-'}</span>
                  </div>
                  <div className="cd-item-body">
                    <div className="cd-item-stat">
                      <IndianRupee size={13} /> <span>{curr}{formatNumber(c.chatRate || 0)}/min</span>
                    </div>
                    <div className="cd-item-stat">
                      <Clock size={13} /> <span>{c.totalMin || 0} min</span>
                    </div>
                    <div className="cd-item-stat cd-item-stat-red">
                      <TrendingDown size={13} /> <span>-{curr}{formatNumber(c.deduction || 0)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="cd-empty">No chat history.</p>
        )}

        {/* Wallet */}
        {activeTab === 'wallet' && (
          <div>
            <div className="cd-subtabs">
              <button onClick={() => setWalletSubTab('transaction')}
                className={`cd-pill ${walletSubTab === 'transaction' ? 'active' : ''}`}>
                Transactions ({data.walletTransaction?.totalCount || 0})
              </button>
              <button onClick={() => setWalletSubTab('payment')}
                className={`cd-pill ${walletSubTab === 'payment' ? 'active' : ''}`}>
                Payments ({data.paymentLogs?.totalCount || 0})
              </button>
            </div>

            {walletSubTab === 'transaction' && (
              wallet.length > 0 ? (
                <div className="cd-txn-list">
                  {wallet.map((w, i) => (
                    <div key={i} className="cd-txn">
                      <div className="cd-txn-info">
                        <span className="cd-txn-desc">{getWalletDesc(w)}</span>
                        <span className="cd-txn-date">{formatDateTime(w.created_at)}</span>
                      </div>
                      <span className={`cd-txn-amount ${parseFloat(w.amount) >= 0 ? 'green' : 'red'}`}>
                        {parseFloat(w.amount) >= 0 ? '+' : ''}{curr}{formatNumber(parseFloat(w.amount || 0))}
                      </span>
                    </div>
                  ))}
                </div>
              ) : <p className="cd-empty">No wallet transactions.</p>
            )}

            {walletSubTab === 'payment' && (
              payment.length > 0 ? (
                <div className="cd-txn-list">
                  {payment.map((p, i) => {
                    const isSuccess = p.paymentStatus === 'success';
                    const isFailed = p.paymentStatus === 'failed';
                    const statusLabel = isSuccess ? 'Success' : isFailed ? 'Failed' : 'Pending';
                    return (
                      <div key={i} className={`cd-txn cd-txn-${isSuccess ? 'success' : isFailed ? 'failed' : 'pending'}`}>
                        <div className="cd-txn-info">
                          <span className="cd-txn-desc">
                            Recharge {p.paymentMode ? `(${p.paymentMode})` : ''}
                            <span className={`cd-badge ${statusClass(statusLabel)}`}>{statusLabel}</span>
                          </span>
                          <span className="cd-txn-date">{formatDateTime(p.created_at)}</span>
                        </div>
                        <div className="cd-txn-right">
                          {!isSuccess && String(p.paymentMode || '').toLowerCase() === 'razorpay' && (
                            <button
                              className="cd-recheck-btn"
                              title="Ask Razorpay whether this was paid; credits the wallet if captured"
                              disabled={!!recheckingId}
                              onClick={() => handleRecheck(p, data.name)}
                            >
                              <RefreshCw size={12} /> {recheckingId === p.id ? 'Checking…' : 'Recheck'}
                            </button>
                          )}
                          <span className={`cd-txn-amount ${isSuccess ? 'green' : isFailed ? 'red' : 'yellow'}`}>
                            {isSuccess ? '+' : ''}{curr}{formatNumber(parseFloat(p.amount || 0))}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : <p className="cd-empty">No payment logs.</p>
            )}
          </div>
        )}

        {/* Order Detail */}
        {activeTab === 'order' && (
          orders.length > 0 ? (
            <div className="cd-grid">
              {orders.map((o, i) => (
                <div key={i} className="cd-item-card">
                  {o.productImage && (
                    <img src={imgSrc(o.productImage)}
                      alt="" className="cd-item-img"
                      onError={e => { e.target.style.display = 'none'; }} />
                  )}
                  <div className="cd-item-product">{o.productCategory} - {o.productName}</div>
                  <div className="cd-item-address">
                    {[o.flatNo, o.locality, o.city, o.state, o.orderCountry, o.orderPincode].filter(Boolean).join(', ')}
                  </div>
                  <div className="cd-item-body">
                    <div className="cd-item-stat"><span>Payable: <strong>{curr}{formatNumber(o.payableAmount || o.productAmount || 0)}</strong></span></div>
                    {o.gstPercent > 0 && <div className="cd-item-stat"><span>GST: {o.gstPercent}%</span></div>}
                    <div className="cd-item-stat"><span>Total: <strong>{curr}{formatNumber(o.totalAmount || o.productAmount || 0)}</strong></span></div>
                  </div>
                  <div className="cd-item-time">{formatDateTime(o.created_at)}</div>
                </div>
              ))}
            </div>
          ) : <p className="cd-empty">No orders.</p>
        )}

        {/* Puja Orders */}
        {activeTab === 'puja' && (
          pujaOrders.length > 0 ? (
            <div className="cd-grid">
              {pujaOrders.map((p, i) => (
                <div key={i} className="cd-item-card">
                  {p.pujaImage && (
                    <img src={imgSrc(p.pujaImage)}
                      alt="" className="cd-item-img"
                      onError={e => { e.target.style.display = 'none'; }} />
                  )}
                  <div className="cd-item-product" style={{ color: '#7c3aed' }}>{p.pujaName || 'Puja'}</div>
                  <div className="cd-item-body">
                    <div className="cd-item-stat"><span>Amount: <strong>{curr}{formatNumber(p.amount || 0)}</strong></span></div>
                  </div>
                  <div className="cd-item-top" style={{ marginTop: 8 }}>
                    <span className={`cd-badge ${statusClass(p.status)}`}>{p.status || '-'}</span>
                    <span className="cd-item-time">{formatDateTime(p.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="cd-empty">No puja orders.</p>
        )}

        {/* Report */}
        {activeTab === 'report' && (
          reportHistory.length > 0 ? (
            <div className="cd-txn-list">
              {reportHistory.map((r, i) => (
                <div key={i} className="cd-txn">
                  <div className="cd-txn-info">
                    <span className="cd-txn-desc">{r.astrologerName}</span>
                    <span className="cd-txn-date">{r.reportTitle || '-'} &middot; {formatDateTime(r.created_at)}</span>
                  </div>
                  <span className="cd-txn-amount purple">{curr}{formatNumber(r.reportRate || 0)}</span>
                </div>
              ))}
            </div>
          ) : <p className="cd-empty">No reports.</p>
        )}

        {/* Following List */}
        {activeTab === 'following' && (
          followers.length > 0 ? (
            <div className="cd-table-wrap">
              <table className="cd-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Profile</th>
                    <th>Name</th>
                    <th>Contact</th>
                    <th>Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {followers.map((f, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td><img src={imgSrc(f.profileImage)} alt="" className="cd-tbl-avatar" onError={e => { e.target.src = fallbackSvg; }} /></td>
                      <td className="cd-tbl-name">{f.name}</td>
                      <td>{f.contactNo || '-'}</td>
                      <td>{formatDate(f.followingDate)}</td>
                      <td>
                        <button onClick={() => navigate(`/admin/astrologers/${f.id}`)} className="cd-icon-btn" title="View">
                          <Eye size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="cd-empty">Not following any astrologers.</p>
        )}

        {/* Notification */}
        {activeTab === 'notification' && (
          notifications.length > 0 ? (
            <div className="cd-table-wrap">
              <table className="cd-table">
                <thead><tr><th>#</th><th>Title</th><th>Description</th></tr></thead>
                <tbody>
                  {notifications.map((n, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td className="cd-tbl-name">{n.title || '-'}</td>
                      <td>{n.description || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="cd-empty">No notifications.</p>
        )}

        {/* Gift */}
        {activeTab === 'gift' && (
          gifts.length > 0 ? (
            <div className="cd-table-wrap">
              <table className="cd-table">
                <thead><tr><th>#</th><th>Astrologer</th><th>Gift</th><th>Amount</th><th>Date</th></tr></thead>
                <tbody>
                  {gifts.map((g, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td>{g.astrologerName || '-'}</td>
                      <td>{g.giftName || '-'}</td>
                      <td className="cd-tbl-red">(-) {curr}{formatNumber(g.giftAmount || g.amount || 0)}</td>
                      <td>{formatDate(g.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="cd-empty">No gifts sent.</p>
        )}
      </div>
    </div>
  );
};

const InfoRow = ({ label, value }) => (
  <div className="cd-irow">
    <span className="cd-irow-label">{label}</span>
    <span className="cd-irow-value">{value || '-'}</span>
  </div>
);

export default CustomerDetail;
